/**
 * useDrumChallenge — the call-and-response game loop.
 *
 * A challenge runs: listen to the phrase, take a count-in, then play it back.
 * Each keypress or pad tap is judged against the nearest unplayed step.
 *
 * Timing is measured on the audio clock rather than `performance.now()`,
 * because that is the clock the samples themselves are scheduled against.
 * Judging against a different clock would let the two drift apart on a long
 * phrase, and the player would be marked late for hits that sounded on time.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import * as Tone from 'tone';
import { DrumChallenge } from '../data/challenges';
import {
  DrumHitRating,
  MAX_HIT_WINDOW_MS,
  comboMultiplier,
  judgeHit,
} from '../lib/scoring';

export type ChallengePhase =
  | 'idle'
  | 'listening'
  | 'countdown'
  | 'playing'
  | 'finished';

/** Feedback for the most recent judgement, for the on-screen flash. */
export interface LastJudgement {
  rating: DrumHitRating;
  padIndex: number;
  /** Bumped on every judgement so repeats of the same rating still animate. */
  nonce: number;
}

export interface DrumRunStats {
  score: number;
  combo: number;
  maxCombo: number;
  perfectCount: number;
  goodCount: number;
  missCount: number;
}

export interface UseDrumChallengeOptions {
  challenge: DrumChallenge | null;
  /** Fires a pad's sample. Supplied by the page, which owns the sample player. */
  playPad: (padIndex: number) => void;
  /** Called once when the run finishes, with the final tally. */
  onFinish?: (stats: DrumRunStats) => void;
}

export interface UseDrumChallengeReturn extends DrumRunStats {
  phase: ChallengePhase;
  /** Beats remaining in the count-in, 0 when not counting. */
  countdownBeat: number;
  /** Pad currently sounding during the demo, for highlighting. */
  promptPadIndex: number | null;
  /**
   * Audio-clock time (ms) that step 0 of the current phrase is due — during the
   * demo and again during the player's turn. Null when nothing is running.
   * The note highway animates against this so it cannot drift from the judging.
   */
  phraseStartMs: number | null;
  /** How many steps have been judged so far. */
  judgedCount: number;
  totalSteps: number;
  lastJudgement: LastJudgement | null;
  /** Play the phrase, then hand over to the player. */
  start: () => void;
  /** Abandon the run and return to idle. */
  reset: () => void;
  /** Register a hit on a pad — from a key or a tap. */
  hit: (padIndex: number) => void;
}

const COUNT_IN_BEATS = 4;

/**
 * Silence before the demo's first stroke, matching the note highway's fall
 * time. Without it the first note would appear already sitting on the hit line
 * instead of travelling to it.
 */
const DEMO_LEAD_IN_MS = 2000;

const EMPTY_STATS: DrumRunStats = {
  score: 0,
  combo: 0,
  maxCombo: 0,
  perfectCount: 0,
  goodCount: 0,
  missCount: 0,
};

/** Milliseconds on the audio clock. */
const nowMs = (): number => Tone.getContext().currentTime * 1000;

export function useDrumChallenge({
  challenge,
  playPad,
  onFinish,
}: UseDrumChallengeOptions): UseDrumChallengeReturn {
  const [phase, setPhase] = useState<ChallengePhase>('idle');
  const [stats, setStats] = useState<DrumRunStats>(EMPTY_STATS);
  const [countdownBeat, setCountdownBeat] = useState(0);
  const [promptPadIndex, setPromptPadIndex] = useState<number | null>(null);
  const [lastJudgement, setLastJudgement] = useState<LastJudgement | null>(null);
  const [judgedCount, setJudgedCount] = useState(0);
  const [phraseStartMs, setPhraseStartMs] = useState<number | null>(null);

  /** Audio-clock time the player's phrase began. */
  const startedAtRef = useRef(0);
  /** One flag per step: has it been judged yet? */
  const judgedRef = useRef<boolean[]>([]);
  /** Every timer in flight, so a reset or unmount can clear them all. */
  const timersRef = useRef<number[]>([]);
  /** Mirrors `stats` for use inside timers without re-subscribing them. */
  const statsRef = useRef<DrumRunStats>(EMPTY_STATS);
  const nonceRef = useRef(0);
  /** Judged-step tally kept outside React state, so it cannot double-count. */
  const judgedTotalRef = useRef(0);
  /**
   * Incremented on every start/reset. Scheduled callbacks capture the value
   * current when they were queued and bail out if it has moved on, so a timer
   * left over from an abandoned run cannot score against the new one.
   */
  const runIdRef = useRef(0);
  /** Run id that has already been reported, so a run finishes exactly once. */
  const finishedRunRef = useRef(-1);
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  const totalSteps = challenge?.steps.length ?? 0;

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn: () => void, delayMs: number) => {
    const runId = runIdRef.current;
    timersRef.current.push(
      window.setTimeout(() => {
        if (runIdRef.current !== runId) return;
        fn();
      }, delayMs),
    );
  }, []);

  const applyStats = useCallback((next: DrumRunStats) => {
    statsRef.current = next;
    setStats(next);
  }, []);

  const reset = useCallback(() => {
    runIdRef.current += 1;
    clearTimers();
    judgedTotalRef.current = 0;
    judgedRef.current = [];
    startedAtRef.current = 0;
    applyStats(EMPTY_STATS);
    setJudgedCount(0);
    setCountdownBeat(0);
    setPromptPadIndex(null);
    setLastJudgement(null);
    setPhraseStartMs(null);
    setPhase('idle');
  }, [applyStats, clearTimers]);

  /** Wrap up: report the tally once, and stop accepting input. */
  const finish = useCallback(() => {
    // Idempotent per run: `finish` is reached from state updaters, which React
    // may invoke more than once, and a double call would submit two scores.
    if (finishedRunRef.current === runIdRef.current) return;
    finishedRunRef.current = runIdRef.current;

    setPhase('finished');
    setPromptPadIndex(null);
    onFinishRef.current?.(statsRef.current);
  }, []);

  const registerMiss = useCallback(
    (padIndex: number) => {
      const prev = statsRef.current;
      applyStats({
        ...prev,
        combo: 0,
        missCount: prev.missCount + 1,
      });
      nonceRef.current += 1;
      setLastJudgement({ rating: 'MISS', padIndex, nonce: nonceRef.current });
    },
    [applyStats],
  );

  /**
   * Steps the player never hit have to be marked missed by something; each one
   * gets a timer for the moment its window closes.
   */
  const armAutoMisses = useCallback(
    (target: DrumChallenge) => {
      target.steps.forEach((step, index) => {
        schedule(() => {
          if (judgedRef.current[index]) return;
          judgedRef.current[index] = true;
          registerMiss(step.padIndex);
          judgedTotalRef.current += 1;
          setJudgedCount(judgedTotalRef.current);
          if (judgedTotalRef.current >= target.steps.length) finish();
        }, step.timeMs + MAX_HIT_WINDOW_MS);
      });
    },
    [finish, registerMiss, schedule],
  );

  const start = useCallback(() => {
    if (!challenge) return;

    runIdRef.current += 1;
    clearTimers();
    judgedTotalRef.current = 0;
    judgedRef.current = new Array(challenge.steps.length).fill(false);
    applyStats(EMPTY_STATS);
    setJudgedCount(0);
    setLastJudgement(null);
    setPhase('listening');

    // The demo's notes fall toward the line just as the player's will, so the
    // phrase is read the same way both times. It starts a look-ahead early so
    // the first note has room to fall rather than appearing already at the line.
    setPhraseStartMs(nowMs() + DEMO_LEAD_IN_MS);

    // 1. Demo the phrase. Each stroke is delayed by the same lead-in as the
    // falling notes, so a note reaches the hit line exactly as it sounds.
    challenge.steps.forEach((step) => {
      schedule(() => {
        playPad(step.padIndex);
        setPromptPadIndex(step.padIndex);
        schedule(() => setPromptPadIndex(null), 140);
      }, DEMO_LEAD_IN_MS + step.timeMs);
    });

    const beatMs = 60_000 / challenge.bpm;
    const phraseMs = challenge.steps[challenge.steps.length - 1].timeMs;
    // A breath between the demo and the count-in, so they do not run together.
    const gapMs = DEMO_LEAD_IN_MS + phraseMs + beatMs;

    // 2. Count the player in.
    setPhase('listening');
    for (let beat = 0; beat < COUNT_IN_BEATS; beat += 1) {
      schedule(() => {
        setPhase('countdown');
        setCountdownBeat(COUNT_IN_BEATS - beat);
        // On the first count, hand the highway the player's phrase origin so
        // their opening notes are already falling by the time the count ends.
        if (beat === 0) {
          setPhraseStartMs(nowMs() + COUNT_IN_BEATS * beatMs);
        }
      }, gapMs + beat * beatMs);
    }

    // 3. Hand over. The player's phrase and the notes they are reading share
    // one origin, so what they see and what is judged cannot disagree.
    schedule(() => {
      setCountdownBeat(0);
      setPhase('playing');
      const startedAt = nowMs();
      startedAtRef.current = startedAt;
      // The highway was already given this origin when the count-in began, and
      // it is re-set here only so judging and display share one exact value.
      setPhraseStartMs(startedAt);
      armAutoMisses(challenge);
    }, gapMs + COUNT_IN_BEATS * beatMs);
  }, [applyStats, armAutoMisses, challenge, clearTimers, playPad, schedule]);

  const hit = useCallback(
    (padIndex: number) => {
      if (!challenge) return;

      // Always sound the pad — the instrument should respond even when the
      // press falls outside the phrase, or the drum feels broken.
      playPad(padIndex);

      if (phase !== 'playing') return;

      const elapsed = nowMs() - startedAtRef.current;

      // Find the nearest step still awaiting a hit, within the outer window.
      let bestIndex = -1;
      let bestDistance = Number.POSITIVE_INFINITY;
      challenge.steps.forEach((step, index) => {
        if (judgedRef.current[index]) return;
        const distance = Math.abs(step.timeMs - elapsed);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestIndex = index;
        }
      });

      // Nothing near this press: a stray tap, ignored rather than punished.
      if (bestIndex === -1 || bestDistance > MAX_HIT_WINDOW_MS) return;

      const step = challenge.steps[bestIndex];
      judgedRef.current[bestIndex] = true;

      // Right timing on the wrong drum voice still counts as a miss — which
      // stroke you play is the point of the exercise.
      if (step.padIndex !== padIndex) {
        registerMiss(padIndex);
      } else {
        const judgement = judgeHit(step.timeMs, elapsed);
        const prev = statsRef.current;

        if (judgement.rating === 'MISS') {
          registerMiss(padIndex);
        } else {
          const combo = prev.combo + 1;
          applyStats({
            ...prev,
            score: prev.score + judgement.points * comboMultiplier(prev.combo),
            combo,
            maxCombo: Math.max(prev.maxCombo, combo),
            perfectCount:
              prev.perfectCount + (judgement.rating === 'PERFECT' ? 1 : 0),
            goodCount: prev.goodCount + (judgement.rating === 'GOOD' ? 1 : 0),
          });
          nonceRef.current += 1;
          setLastJudgement({
            rating: judgement.rating,
            padIndex,
            nonce: nonceRef.current,
          });
        }
      }

      judgedTotalRef.current += 1;
      setJudgedCount(judgedTotalRef.current);
      if (judgedTotalRef.current >= challenge.steps.length) finish();
    },
    [applyStats, challenge, finish, phase, playPad, registerMiss],
  );

  // Only tear down on unmount. Tying this to `challenge` would let a re-render
  // cancel the timers of a run that is still in progress.
  useEffect(() => clearTimers, [clearTimers]);

  return {
    ...stats,
    phase,
    countdownBeat,
    promptPadIndex,
    phraseStartMs,
    judgedCount,
    totalSteps,
    lastJudgement,
    start,
    reset,
    hit,
  };
}
